import { useEffect, useRef, useState, useMemo } from 'react';
import { useParams, useLocation, useHistory, useRouteMatch } from 'react-router';
import queryString from 'query-string';
export function useWhyDidYouUpdate(name, props) {
  const previousProps = useRef<any>();

  useEffect(() => {
    if (previousProps.current) {
      const allKeys = Object.keys({ ...previousProps.current, ...props });

      const changesObj = {};

      allKeys.forEach(key => {
        if (previousProps[key] !== props[key]) {
          changesObj[key] = {
            from: previousProps.current![key],
            to: props[key]
          };
        }
      });

      if (Object.keys(changesObj).length) {
        console.log('[why-did-you-update', name, changesObj);
      }
    }

    previousProps.current = props;
  });
}
/**
 * 绑定事件
 * @param eventName 事件名称
 * @param handler 回调函数
 * @param element 触发element
 */
export function useEventListener(eventName, handler, element = window) {
  const savedHandler = useRef<any>();

  useEffect(() => {
    savedHandler.current = handler;
  }, [handler]);

  useEffect(() => {
    const isSupported = element && element.addEventListener;
    if (!isSupported) return;

    const eventListener = event => savedHandler.current!(event);

    element.addEventListener(eventName, eventListener);

    return () => {
      element.removeEventListener(eventName, eventListener);
    };
  }, [eventName, element]);
}

/**
 *
 * @param [string] key
 * @param [any] initialValue
 */
export function useLocalStorage(key, initialValue) {
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.log(error);
      return initialValue;
    }
  });

  const setValue = value => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      console.log(error);
    }
  };
  return [storedValue, setValue];
}

export function useInbounce() {
  useEffect(() => {
    const inobounce = require('inobounce');
    if (window.YwkHybrid.os.ios) {
      inobounce.enable();
    }
    return () => {
      inobounce.disable();
    };
  }, []);
}

/**
 * 使body可滚动，iOS上与useInbounce一起使用会无效
 *
 * @export
 */
export function useAutoHeight() {
  useEffect(() => {
    document.body.classList.add('autoheight');
    return () => {
      document.body.classList.remove('autoheight');
    };
  }, []);
}

/**
 * 给body增加背景色
 *
 * @export
 */
export function useBodyBackground() {
  useEffect(() => {
    document.body.classList.add('background');
    return () => {
      document.body.classList.remove('background');
    };
  }, []);
}

/**
 * 关闭body的滚动
 *
 * @export
 * @param {boolean} visible
 */
export function useLockScroll(visible: boolean) {
  useEffect(() => {
    if (visible) {
      document.body.classList.add('noscroll');
    } else {
      document.body.classList.remove('noscroll');
    }

    return () => {
      document.body.classList.remove('noscroll');
    };
  }, [visible]);
}

export function useStopTouchPropagation(container) {
  useEffect(() => {
    const prevent = e => {
      e.stopPropagation();
    };

    if (container) {
      container.addEventListener('touchmove', prevent, { passive: false });
      container.addEventListener('touchstart', prevent, { passive: false });
      container.addEventListener('touchend', prevent, { passive: false });
      container.addEventListener('touchcancel', prevent, { passive: false });
    }
    return () => {
      if (container) {
        container.removeEventListener('touchmove', prevent);
        container.removeEventListener('touchstart', prevent);
        container.removeEventListener('touchend', prevent);
        container.removeEventListener('touchcancel', prevent);
      }
    };
  }, [container]);
}

// Hook
export function useRouter() {
  const params = useParams();
  const location = useLocation();
  const history = useHistory();
  const match = useRouteMatch();

  // Return our custom router object
  // Memoize so that a new object is only returned if something changes
  return useMemo(() => {
    return {
      // For convenience add push(), replace(), pathname at top level
      push: history.push,
      replace: history.replace,
      pathname: location.pathname,
      // Merge params and parsed query string into single "query" object
      // so that they can be used interchangeably.
      // Example: /:topic?sort=popular -> { topic: "react", sort: "popular" }
      query: {
        ...queryString.parse(location.search), // Convert string to object
        ...params
      },
      // Include match, location, history objects so we have
      // access to extra React Router functionality if needed.
      match,
      location,
      history
    };
  }, [params, match, location, history]);
}
/**
 * hook中使用interval
 * @param callback {function}
 * @param delay 延迟
 */
export function useInterval(callback, delay) {
  const savedCallback: any = useRef();

  // 保存新回调
  useEffect(() => {
    savedCallback.current = callback;
  });

  // 建立 interval
  useEffect(() => {
    function tick() {
      savedCallback.current();
    }
    if (delay !== null) {
      let id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
}

/**
 * 页面input focus时，阻止其他input or textarea可用（修复iOS箭头切换input）
 */
export function useInputFocus(...rest) {
  useEffect(() => {
    $('input, textarea').on('focus', function () {
      $('input, textarea').not(this).attr('disabled', 'true');
    });
    $('input, textarea').on('blur', function () {
      $('input, textarea').removeAttr('disabled');
    });
    return () => {
      $('input, textarea').off('focus');
      $('input, textarea').off('blur');
    };
  }, rest);
}
